import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AdminLayout from "../../components/admin/AdminLayout";
import AbandonedCartsWidget from "../../components/admin/AbandonedCartsWidget";
import StorageHealthWidget from "../../components/admin/StorageHealthWidget";
import { api, money } from "../../lib/api";
import { Package, Car, ShoppingBag, DollarSign, AlertTriangle } from "lucide-react";

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/admin/stats").then(({ data }) => setStats(data));
  }, []);

  return (
    <AdminLayout title="Dashboard">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-10" data-testid="admin-stats">
        <Stat label="Products" value={stats?.total_products ?? "—"} icon={Package} />
        <Stat label="Vehicles" value={stats?.total_vehicles ?? "—"} icon={Car} />
        <Stat label="Orders" value={stats?.total_orders ?? "—"} icon={ShoppingBag} />
        <Stat label="Revenue" value={stats ? money(stats.revenue) : "—"} icon={DollarSign} />
        <Stat label="Low stock" value={stats?.low_stock ?? "—"} icon={AlertTriangle} highlight={stats?.low_stock > 0} />
      </div>

      <AbandonedCartsWidget />
      <StorageHealthWidget />

      <div className="bg-white border border-zinc-200 rounded-md">
        <div className="px-6 py-4 border-b border-zinc-200 flex items-center justify-between">
          <h2 className="font-display tracking-tight">Recent orders</h2>
          <Link to="/admin/orders" className="text-xs text-zinc-500 hover:text-zinc-900">View all →</Link>
        </div>
        <table className="w-full text-sm" data-testid="admin-recent-orders">
          <thead className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 bg-zinc-50">
            <tr>
              <th className="text-left px-6 py-3">Order</th>
              <th className="text-left px-6 py-3">Customer</th>
              <th className="text-left px-6 py-3">Items</th>
              <th className="text-left px-6 py-3">Payment</th>
              <th className="text-right px-6 py-3">Total</th>
            </tr>
          </thead>
          <tbody>
            {(stats?.recent_orders || []).length === 0 && (
              <tr><td colSpan={5} className="text-center text-zinc-500 py-10">No orders yet.</td></tr>
            )}
            {(stats?.recent_orders || []).map((o) => (
              <tr key={o.id} className="border-t border-zinc-100 hover:bg-zinc-50">
                <td className="px-6 py-3 font-mono text-xs">{o.id.slice(0, 8)}</td>
                <td className="px-6 py-3">{o.customer_email || "—"}</td>
                <td className="px-6 py-3">{o.items?.length || 0}</td>
                <td className="px-6 py-3"><StatusPill status={o.payment_status} /></td>
                <td className="px-6 py-3 text-right font-mono">{money(o.subtotal)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </AdminLayout>
  );
}

function Stat({ label, value, icon: Icon, highlight }) {
  return (
    <div className={`bg-white border ${highlight ? "border-amber-300" : "border-zinc-200"} rounded-md p-5`}>
      <div className="flex items-center justify-between mb-3">
        <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-bold">{label}</div>
        <Icon size={14} className={highlight ? "text-amber-500" : "text-zinc-400"} />
      </div>
      <div className="text-2xl font-mono font-medium">{value}</div>
    </div>
  );
}

export function StatusPill({ status }) {
  const map = {
    paid: "bg-emerald-50 text-emerald-700 border-emerald-200",
    pending: "bg-amber-50 text-amber-700 border-amber-200",
    failed: "bg-red-50 text-red-700 border-red-200",
    expired: "bg-zinc-50 text-zinc-600 border-zinc-200",
  };
  return <span className={`inline-block text-[10px] uppercase tracking-[0.2em] font-bold px-2 py-1 border rounded ${map[status] || "bg-zinc-50 text-zinc-600 border-zinc-200"}`}>{status}</span>;
}
